import { css, useTheme } from "@emotion/react";
import { faCircleCheck, faDownload } from "@fortawesome/pro-solid-svg-icons";

import { useCustomerLatestQuote } from "@@/backoffice/for-providers/quotes/hooks/use-quote";
import { useProvider } from "@@/providers/state/queries/provider/use-provider";
import { useQueryOrParam } from "@@/shared/use-query-or-param";

import { PageLoad } from "@@/pages/page-load";
import { BrandingProvider } from "@@/routes/shared/branding-context";
import { ButtonQuaternaryLight } from "@@/shared/buttons_v2/button-quaternary-light";
import {
    HorizontalDivider,
    HorizontalLine,
    VerticalDivider,
} from "@@/shared/dividers";
import { backendHost } from "@@/shared/fetch-client";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { ForEach } from "@@/shared/for-each";
import { GlobalMaxWidthCss } from "@@/shared/global-max-width";
import { Icon } from "@@/shared/icons/icon";
import { LayoutCell } from "@@/shared/layout-cell";
import { LayoutGrid } from "@@/shared/layout-grid";
import { usePageBreakpoint } from "@@/shared/responsiveness/use-page-breakpoint";
import { PriceTextBox, TextBox } from "@@/shared/text";
import { DateTextBox, TimeRangeTextBox } from "@@/shared/text/date-text-box";
import { ProviderLogoLinkCircle } from "@@/store-fronts/towni/sections/providers/provider-logo-link-circle";

import { useViewQuoteCommandMutation } from "@@/backoffice/for-providers/quotes/hooks/use-view-quote-mutation";
import {
    MagicAcceptQuoteModalContextProvider,
    useMagicAcceptQuoteModalContext,
} from "@@/backoffice/for-providers/quotes/magic-accept-quote-modal";
import QuoteStatusBanner from "@@/backoffice/for-providers/quotes/quote-status-banner";
import { CompanyDetails } from "@@/receipts/shared/company-details";
import { GlobalBackgroundDiv } from "@@/routes/products/$productId/global_background-branding";
import { ButtonBlack } from "@@/shared/buttons_v2/button-gray";
import { Conditional } from "@@/shared/conditional";
import { ShowOrTextShimmer } from "@@/shared/show-or-spin";
import type {
    HeadQuoteItem,
    Provider,
    Quote,
    SubBasicItem,
} from "@towni/common";
import {
    generateComboId,
    isQuoteId,
    multiplyPrice,
    parseProviderPosterImage,
    sumPrices,
    translation,
    viewQuoteCommandFactory,
} from "@towni/common";
import { useEffect } from "react";

const logoLinkCss = css({
    color: "#ffffff",
    fontStyle: "italic",
    ":hover": {
        color: "palegreen",
    },
});

const getExpiringViewerToken = (
    quoteId: string,
    maxAgeMinutes: number,
): string => {
    const key = `QUOTE_VIEW_TOKEN_${quoteId}`;
    const value = localStorage.getItem(key);

    if (value) {
        const [token, timestamp] = value.split("::");
        const age = Date.now() - parseInt(timestamp, 10);
        if (age < maxAgeMinutes * 60 * 1000) return token;
    }

    const newToken = generateComboId();
    const stamped = `${newToken}::${Date.now()}`;
    localStorage.setItem(key, stamped);
    return newToken;
};
/**
 * Customer-facing page to display a quote
 */
const CustomerQuoteDisplayPage = () => {
    const quoteId = useQueryOrParam("quoteId");

    const quoteQuery = useCustomerLatestQuote(
        isQuoteId(quoteId) ? quoteId : undefined,
    );

    if (!quoteQuery.data) {
        return <PageLoad />;
    }

    const quote = quoteQuery.data;

    return (
        <BrandingProvider providerId={quote.providerId}>
            <MagicAcceptQuoteModalContextProvider>
                <InnerQuoteDisplayPage quote={quote} />
            </MagicAcceptQuoteModalContextProvider>
        </BrandingProvider>
    );
};

type Props = {
    quote: Quote;
};

// Function to determine quote status
const getQuoteStatus = (quote: Quote) => {
    return quote.state;
};

const InnerQuoteDisplayPage = ({ quote }: Props) => {
    const { mutate: trackView } = useViewQuoteCommandMutation();

    useEffect(() => {
        if (quote._id === undefined) return;
        const token = getExpiringViewerToken(quote._id, 3600);

        trackView(
            viewQuoteCommandFactory({
                quoteId: quote._id,
                providerId: quote.providerId,
                token,
            }),
        );
    }, [quote, trackView]);

    const isMedium = usePageBreakpoint({ when: "M" });
    const isLarge = usePageBreakpoint({ when: "L" });
    const [provider, _providerQuery] = useProvider(quote.providerId);
    if (!provider) return <PageLoad />;

    return (
        <div
            css={[
                {
                    width: "100%",
                    height: "100%",
                    overflowX: "hidden",
                    overflowY: "scroll",
                },
            ]}>
            <GlobalBackgroundDiv fallBackToReceipt />
            <FlexColumn
                mainAxis="stretch"
                crossAxis="center"
                css={{
                    width: "100%",
                }}>
                <GlobalMaxWidthCss />
                <VerticalDivider XXL />

                <FlexRow
                    mainAxis="flex-end"
                    css={{ width: isMedium ? 440 : isLarge ? 600 : "93%" }}>
                    <ButtonQuaternaryLight
                        onClick={() => {
                            alert("Kan inte skriva ut, låg cyan i skrivaren");
                        }}
                        css={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}>
                        <Icon icon={faDownload} color="white" />
                        <HorizontalDivider S />
                        <TextBox
                            text={{ sv: "Ladda ner PDF", en: "Download PDF" }}
                            color="white"
                        />
                    </ButtonQuaternaryLight>
                </FlexRow>

                <VerticalDivider S />

                <QuoteDisplayContent quote={quote} />
                <VerticalDivider XXL />

                <a
                    href={`https://towni.se`}
                    title="Towni"
                    target="_blank"
                    rel="noreferrer"
                    css={logoLinkCss}>
                    <img
                        src={`${backendHost}/public/storage/towni-brand/logos/towni-logo-hz-white-on-dark-medium.png`}
                        alt="Towni logo"
                        style={{ height: 40, opacity: 0.3 }}
                    />
                </a>

                <VerticalDivider XXL />
                <VerticalDivider XXL />
            </FlexColumn>
        </div>
    );
};

const QuoteDisplayContent = ({ quote }: Props) => {
    const isMedium = usePageBreakpoint({ when: "M" });
    const isLarge = usePageBreakpoint({ when: "L" });
    const theme = useTheme();
    const [provider, _providerQuery] = useProvider(quote.providerId);
    const modal = useMagicAcceptQuoteModalContext();
    if (!provider) return <PageLoad />;

    const poster = parseProviderPosterImage(provider);
    const quoteStatus = getQuoteStatus(quote);

    return (
        <LayoutGrid
            css={{
                backgroundColor: theme.colors.default.background.asString,
                width: isMedium ? 440 : isLarge ? 600 : "93%",
                minWidth: 340,
                borderWidth: 0,
                borderRadius: 10,
                boxShadow: "0 0 20px 0 rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                transition: "all 0.2s ease",
            }}>
            <LayoutCell>
                {/* Header with provider cover image */}
                <FlexRow
                    mainAxis="center"
                    crossAxis="center"
                    background={{
                        imageReference: poster,
                        fit: "COVER",
                    }}
                    css={{
                        minHeight: 240,
                        flex: 1,
                        position: "relative",
                    }}>
                    {/* Status overlay for accepted quotes */}
                    {quoteStatus === "ACCEPTED" && (
                        <div
                            css={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(0, 128, 0, 0.2)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}>
                            <div
                                css={{
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                    padding: "6px 16px",
                                    borderRadius: 20,
                                    transform: "rotate(-10deg)",
                                }}>
                                <TextBox
                                    text={translation({
                                        sv: "ACCEPTERAD",
                                        en: "ACCEPTED",
                                    })}
                                    size="M"
                                    weight="900"
                                    color={theme.colors.primary.dark}
                                />
                            </div>
                        </div>
                    )}
                </FlexRow>

                {/* Provider info */}
                <FlexColumn>
                    <VerticalDivider XXS />
                    <FlexRow css={{ padding: 15 }}>
                        <ProviderLogoLinkCircle
                            provider={provider}
                            disableLink
                            hideTitle
                            size="3XS"
                        />
                        <HorizontalDivider />
                        <FlexColumn>
                            <TextBox
                                text={provider.meta.title}
                                weight="900"
                                size="M"
                                color={theme.colors.black.light10}
                            />
                            <TextBox
                                text={provider.meta.subtitle}
                                size="S"
                                color={theme.colors.black.light10}
                            />
                        </FlexColumn>
                    </FlexRow>
                </FlexColumn>

                <HorizontalLine size={1} />
                <VerticalDivider ML />

                {/* Status Banner */}
                <Conditional when={quoteStatus === "DRAFT"}>
                    <FlexColumn css={{ paddingLeft: 20, paddingRight: 20 }}>
                        <QuoteStatusBanner status={quoteStatus} />
                    </FlexColumn>
                </Conditional>

                {/* Quote content section */}
                <QuoteInfoSection quote={quote} provider={provider} />
                <ShowOrTextShimmer
                    showWhen={provider}
                    shimmerCss={{
                        paddingTop: 30,
                    }}>
                    {provider => (
                        <FlexColumn css={{ paddingLeft: 20, paddingRight: 20 }}>
                            <VerticalDivider L />
                            <CompanyDetails
                                provider={provider}
                                options={{
                                    hideProviderTitleAndId: true,
                                }}
                            />
                            <VerticalDivider L />
                        </FlexColumn>
                    )}
                </ShowOrTextShimmer>
            </LayoutCell>

            {/* Action section - Only show for published quotes that aren't accepted yet */}
            {quoteStatus === "PUBLISHED" && (
                <LayoutCell
                    className="content-center justify-items-center"
                    css={{
                        padding: 30,
                        paddingTop: 35,
                        backgroundColor: theme.colors.primary.light.asString,
                    }}>
                    <ButtonBlack
                        onClick={() => {
                            modal.show({
                                quote: quote,
                            });
                        }}
                        css={{ width: "auto", height: 55 }}>
                        <FlexRow>
                            <Icon
                                icon={faCircleCheck}
                                color={theme.colors.white.asString}
                            />
                            <HorizontalDivider S />
                            <TextBox
                                color={theme.colors.white.asString}
                                size={1.1}
                                text={translation({
                                    sv: "Acceptera och boka nu",
                                    en: "Accept and book now",
                                })}
                            />
                        </FlexRow>
                    </ButtonBlack>
                    <TextBox
                        text={translation({
                            sv: "Genom att acceptera offerten godkänner du även våra allmänna villkor.",
                            en: "By accepting this quote, you also agree to our terms and conditions.",
                        })}
                        size={0.725}
                        align="center"
                        color={theme.colors.default.text.withAlpha(0.3)}
                        padding={{ all: 10, bottom: 0 }}
                    />
                </LayoutCell>
            )}

            {/* For accepted quotes, show confirmation message instead of action button */}
            {quoteStatus === "ACCEPTED" && (
                <LayoutCell
                    css={{
                        padding: 30,
                        paddingTop: 35,
                        backgroundColor:
                            theme.colors.primary.light.light95.asString,
                    }}>
                    <div className="flex flex-col items-center space-y-2">
                        <TextBox
                            text={translation({
                                sv: "Tack och varmt välkommen!",
                                en: "Thank you and welcome!",
                            })}
                            size={1.1}
                            weight="700"
                            color={theme.colors.primary.dark}
                        />
                        <VerticalDivider XS />
                        <div className="flex w-full items-center justify-center">
                            <div className="rounded-full bg-green-100 px-4 py-1 text-green-800">
                                <TextBox
                                    text={translation({
                                        sv: "Accepterad",
                                        en: "Accepted",
                                    })}
                                    size={"S"}
                                    weight="600"
                                />
                            </div>
                            <div className="mx-2 text-gray-400">•</div>

                            <DateTextBox
                                date={quote.accepted?.acceptedAt}
                                size="S"
                                weight="600"
                                color={theme.colors.black.light10}
                            />
                        </div>

                        <TextBox
                            text={translation({
                                sv: quote.accepted
                                    ? quote.accepted.name
                                    : "Kundnamn saknas",
                            })}
                            size={0.9}
                            align="center"
                            color={theme.colors.primary.dark.withAlpha(0.7)}
                        />
                        <TextBox
                            text={translation({
                                sv: quote.accepted ? quote.accepted.email : "-",
                            })}
                            size={0.9}
                            align="center"
                            color={theme.colors.primary.dark.withAlpha(0.7)}
                        />
                    </div>
                </LayoutCell>
            )}

            {/* For draft quotes, show draft message */}
            {quoteStatus === "DRAFT" && (
                <LayoutCell
                    css={{
                        padding: 30,
                        paddingTop: 35,
                        backgroundColor: theme.colors.black.light95.asString,
                    }}>
                    <FlexColumn crossAxis="center">
                        <TextBox
                            text={translation({
                                sv: "Detta är ett utkast",
                                en: "This is a draft",
                            })}
                            size={1.1}
                            weight="700"
                            color={theme.colors.black.light30}
                        />
                        <VerticalDivider M />
                        <TextBox
                            text={translation({
                                sv: "Offerten har inte skickats till kunden än",
                                en: "The quote has not been sent to the customer yet",
                            })}
                            size={0.9}
                            align="center"
                            color={theme.colors.black.light40}
                        />
                    </FlexColumn>
                </LayoutCell>
            )}
        </LayoutGrid>
    );
};

/**
 * Quote information section with provider and customer details
 */
const QuoteInfoSection = ({
    quote,
    provider,
}: {
    quote: Quote;
    provider: Provider;
}) => {
    const theme = useTheme();

    // Calculate totals
    const totalQuote = sumPrices(
        (quote.items ?? []).flatMap(item => {
            return item.subItems.map(subItem => {
                return multiplyPrice(subItem.pricePerItem, subItem.quantity);
            });
        }),
    );

    return (
        <FlexColumn
            mainAxis="flex-start"
            crossAxis="stretch"
            css={{
                flex: 1,
                padding: 20,
            }}>
            {/* Quote title */}
            <TextBox
                text={quote.title}
                size={1.9}
                weight="900"
                align="center"
                css={{
                    flex: 1,
                }}
            />

            <VerticalDivider M />

            {/* Quote reference and validity */}
            <FlexRow mainAxis="space-between" crossAxis="center">
                <Conditional when={!!quote.reference} else={() => <div />}>
                    <TextBox
                        text={{
                            sv: `Referens: #${quote.reference ?? "Saknas"}`,
                            en: `Reference: #${quote.reference ?? "Missing"}`,
                        }}
                        size="S"
                        color={theme.colors.black.light40}
                    />
                </Conditional>
                <Conditional when={quote.state !== "ACCEPTED"}>
                    <FlexColumn
                        padding={{ all: 12 }}
                        css={{
                            backgroundColor:
                                theme.colors.black.light95.asString,
                            border: `1px solid ${theme.colors.black.light90.asString}`,
                            borderRadius: 4,
                        }}>
                        <TextBox
                            text={{
                                sv: "Offerten är giltig till",
                                en: "Quote valid until",
                            }}
                            size="XS"
                            color={theme.colors.black.light40}
                        />
                        <DateTextBox
                            date={quote.valid.end}
                            size="S"
                            weight="600"
                            color={theme.colors.black.light10}
                        />
                    </FlexColumn>
                </Conditional>
            </FlexRow>

            <VerticalDivider L />

            {/* Customer info */}
            <FlexRow css={{ padding: 10 }}>
                <FlexColumn css={{ width: "100%" }}>
                    <TextBox
                        text={{ sv: "Från", en: "From" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />

                    <VerticalDivider XS />

                    <TextBox
                        text={provider.meta.title}
                        size="M"
                        weight="600"
                        color={theme.colors.black.light10}
                    />

                    <VerticalDivider XXS />

                    <TextBox
                        text={provider.emails[0].emailAddress}
                        size="S"
                        color={theme.colors.black.light40}
                    />

                    <VerticalDivider XXS />

                    <TextBox
                        text={provider.phones[0].phoneNumber}
                        size="S"
                        color={theme.colors.black.light40}
                    />
                </FlexColumn>

                <FlexColumn css={{ width: "100%" }}>
                    <TextBox
                        text={{ sv: "Till", en: "To" }}
                        size="S"
                        weight="600"
                        color={theme.colors.black.light40}
                    />

                    <VerticalDivider XXS />

                    <TextBox
                        text={`${quote.customer.firstName} ${quote.customer.lastName || ""}`}
                        size="M"
                        weight="600"
                        color={theme.colors.black.light10}
                    />

                    <TextBox
                        text={quote.customer.emailAddress}
                        size="S"
                        color={theme.colors.black.light40}
                    />

                    {quote.customer.businessOrgNo && (
                        <TextBox
                            text={`Org.nummer: ${quote.customer.businessOrgNo}`}
                            size="S"
                            color={theme.colors.black.light40}
                        />
                    )}
                </FlexColumn>
            </FlexRow>

            <VerticalDivider M />
            <HorizontalLine size={1} css={{ opacity: 0.1 }} />
            <VerticalDivider M />

            {/* Quote items */}
            <ForEach itemOf={quote.items ?? []}>
                {(item: HeadQuoteItem) => {
                    return (
                        <FlexColumn css={{ marginBottom: 20 }}>
                            {/* Group header */}
                            <FlexColumn
                                css={{
                                    padding: 10,
                                    backgroundColor:
                                        theme.colors.black.light97.asString,
                                    borderRadius: 4,
                                }}>
                                <FlexRow
                                    mainAxis="space-between"
                                    crossAxis="center">
                                    <TextBox
                                        text={item.meta.title}
                                        size="M"
                                        weight="600"
                                        color={theme.colors.black.light10}
                                    />

                                    <TimeRangeTextBox
                                        color={theme.colors.black.light40}
                                        timeRange={item.time}
                                        size="S"
                                    />
                                </FlexRow>

                                <TextBox
                                    text={item.meta.description}
                                    size="S"
                                    color={theme.colors.black.light40}
                                />
                            </FlexColumn>

                            {/* Sub-items */}
                            <ForEach itemOf={item.subItems}>
                                {(subItem: SubBasicItem) => {
                                    const itemTotal = multiplyPrice(
                                        subItem.pricePerItem,
                                        subItem.quantity,
                                    );

                                    // Skip resource items in display to customers
                                    if (subItem.type === "RESOURCE") {
                                        return null;
                                    }

                                    //Bookable parent row not needed
                                    if (subItem.type === "BOOKABLE") {
                                        return null;
                                    }

                                    return (
                                        <FlexRow
                                            mainAxis="space-between"
                                            padding={{ all: 10 }}
                                            css={{
                                                borderBottom: `1px solid ${theme.colors.black.light95.asString}`,
                                            }}>
                                            <FlexColumn>
                                                <TextBox
                                                    text={subItem.title}
                                                    size="S"
                                                    color={
                                                        theme.colors.black
                                                            .light10
                                                    }
                                                />
                                                <TextBox
                                                    text={`${subItem.quantity} st`}
                                                    size="XS"
                                                    color={
                                                        theme.colors.black
                                                            .light40
                                                    }
                                                />
                                            </FlexColumn>

                                            <PriceTextBox
                                                price={itemTotal}
                                                size="S"
                                                color={
                                                    theme.colors.black.light10
                                                }
                                            />
                                        </FlexRow>
                                    );
                                }}
                            </ForEach>
                        </FlexColumn>
                    );
                }}
            </ForEach>

            <VerticalDivider L />

            {/* Totals section */}
            <FlexColumn
                css={{
                    borderTop: `1px solid ${theme.colors.black.light90.asString}`,
                    paddingTop: 16,
                }}>
                <FlexRow mainAxis="space-between" padding={{ bottom: 8 }}>
                    <TextBox
                        text={{ sv: "Delsumma", en: "Subtotal" }}
                        size="S"
                        color={theme.colors.black.light40}
                    />
                    <PriceTextBox
                        valueToFormat="AMOUNT_EXCLUDING_VAT"
                        price={totalQuote}
                        size="S"
                        color={theme.colors.black.light40}
                    />
                </FlexRow>

                <FlexRow mainAxis="space-between" padding={{ bottom: 8 }}>
                    <TextBox
                        text={{ sv: "Moms", en: "VAT" }}
                        size="S"
                        color={theme.colors.black.light40}
                    />
                    <PriceTextBox
                        valueToFormat="VAT_AMOUNT"
                        price={totalQuote}
                        size="S"
                        color={theme.colors.black.light40}
                    />
                </FlexRow>

                <FlexRow
                    mainAxis="space-between"
                    padding={{ top: 12 }}
                    css={{
                        borderTop: `1px solid ${theme.colors.black.light90.asString}`,
                    }}>
                    <TextBox
                        text={{ sv: "Totalt att betala", en: "Total to pay" }}
                        size="M"
                        weight="600"
                        color={theme.colors.black.light10}
                    />
                    <PriceTextBox
                        price={totalQuote}
                        size="L"
                        weight="600"
                        color={theme.colors.black.light10}
                    />
                </FlexRow>
            </FlexColumn>
        </FlexColumn>
    );
};

export { CustomerQuoteDisplayPage, QuoteDisplayContent };
